import { useState, useEffect } from "react";

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────
// Reads the same state the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Rich text helper ─────────────────────────────────────────────────────────
// Stores content as plain strings with a simple bold marker: **text**
// The Rich component renders them inline.

function Rich({ children }) {
  if (typeof children !== "string") return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}

// ─── Content Data (plain JS — no JSX) ────────────────────────────────────────

const sections = [
  {
    title: "1. Our Monetisation Model",
    paragraphs: [
      "Travel Rethink Ways is a modern travel intelligence platform that provides curated destination insights, travel guides, unique stays, deals, and brand stories. To sustain our operations and continue delivering high-quality travel content, we may engage in affiliate marketing, sponsored collaborations, advertising partnerships, and other commercial arrangements.",
    ],
    extraParagraphs: [
      "We are currently planning and expanding affiliate relationships across various travel and hospitality sectors. These relationships may evolve over time.",
    ],
  },
  {
    title: "2. Affiliate Relationships & Material Connections",
    paragraphs: [
      "We may have a **material connection with** certain brands, products, services, tourism boards, or partners mentioned on our website. A material connection means we may receive financial compensation, commissions, referral fees, complimentary services, hosted stays, promotional consideration, or other benefits when users interact with specific links or recommendations.",
    ],
    extraLabel: "Affiliate partnerships may include, but are not limited to:",
    bullets: [
      "Hotel and resort bookings",
      "Boutique and experiential stays",
      "Flight reservations",
      "Travel insurance providers",
      "Tours, excursions, and activities",
      "Cruise bookings",
      "Visa services",
      "Travel gear and accessories",
      "Credit cards and financial travel products",
      "Travel software and digital tools",
      "Tourism boards and destination campaigns",
      "Hospitality brands and travel agencies",
    ],
    extraParagraphs: [
      "When you click on an affiliate link and complete a qualifying action (such as making a booking or purchase), we may earn a commission. **There is no additional cost to you.** The price you pay remains the same whether you use our affiliate link or visit the provider directly.",
    ],
  },
  {
    title: "3. Identification of Affiliate Links",
    paragraphs: [
      "Affiliate links may or may not be explicitly labeled individually. However, content containing affiliate relationships may include disclosures such as:",
    ],
    bullets: [
      '"Affiliate Link"',
      '"Partner Link"',
      '"In Partnership With"',
      '"Sponsored"',
      '"Paid Collaboration"',
    ],
    extraParagraphs: [
      "In some cases, disclosure may appear within the article, at the beginning or end of content, or through a general site-wide disclosure statement such as this page.",
      "We strive to ensure disclosures are clear and reasonably visible to readers.",
    ],
  },
  {
    title: "4. Sponsored & Paid Content",
    paragraphs: [
      "Travel Rethink Ways may publish sponsored content, branded features, curated partner stories, destination campaigns, or promotional collaborations.",
    ],
    extraLabel: "Sponsored arrangements may involve:",
    bullets: [
      "Financial compensation",
      "Complimentary stays or hosted experiences",
      "Product samples",
      "Marketing partnerships",
      "Performance-based commissions",
    ],
    extraListLabel: "All sponsored content will be clearly identified using appropriate disclosure language such as:",
    extraList: [
      '"Sponsored"',
      '"Brand Feature"',
      '"In Collaboration With"',
      '"Partnered With"',
    ],
    extraParagraphs: [
      "We do not intentionally present sponsored content as independent editorial material without disclosure.",
    ],
  },
  {
    title: "5. Editorial Integrity & Curation",
    paragraphs: [
      "We aim to provide meaningful, experience-driven, and curated travel intelligence. However, users should understand the following:",
    ],
    bullets: [
      "Some curated recommendations may be influenced by partnership relationships.",
      "Certain deals, featured stays, or highlighted brands may be selected based on affiliate or commercial arrangements.",
      "Sponsored partnerships may influence the inclusion or prominence of certain brands.",
    ],
    extraLabel: "That said, we strive to ensure that:",
    extraList: [
      "Information remains accurate to the best of our knowledge.",
      "Content aligns with our platform's mission of enabling smarter travel decisions.",
      "Sponsored features are transparently disclosed.",
    ],
    extraParagraphs: [
      "We do not guarantee that all listed options represent the entire market of available providers, nor do we guarantee that non-partner brands will receive equal visibility.",
    ],
  },
  {
    title: "6. Deals, Pricing & Availability Disclaimer",
    paragraphs: [
      "Travel-related information is subject to frequent changes. This includes, but is not limited to:",
    ],
    bullets: [
      "Pricing fluctuations",
      "Seasonal discounts",
      "Visa requirements",
      "Entry regulations",
      "Government travel advisories",
      "Airline policies",
      "Hotel policies",
    ],
    extraParagraphs: [
      "While we aim to provide timely and helpful information, we cannot guarantee that all content reflects real-time updates. Users are responsible for verifying final prices, terms, and requirements directly with service providers before making bookings or travel decisions.",
    ],
  },
  {
    title: "7. No Extra Cost to You",
    paragraphs: [
      "Using affiliate links on Travel Rethink Ways does not increase your purchase price. Commissions are paid by the partner company as a marketing expense.",
    ],
    extraLabel: "Your support through affiliate links helps us:",
    bullets: [
      "Maintain and improve the platform",
      "Produce curated travel guides",
      "Conduct destination research",
      "Build travel intelligence resources",
      "Feature meaningful travel brands",
    ],
    extraParagraphs: [
      "We genuinely appreciate this support.",
    ],
  },
  {
    title: "8. User Responsibility",
    paragraphs: [
      "All travel decisions involve personal judgment and financial commitment. Travel Rethink Ways does not assume responsibility for:",
    ],
    bullets: [
      "Booking outcomes",
      "Service quality provided by third-party vendors",
      "Travel disruptions",
      "Visa rejections",
      "Pricing changes",
      "Refund disputes",
    ],
    extraParagraphs: [
      "Users are encouraged to conduct independent research and evaluate suitability before making travel-related decisions.",
    ],
  },
  {
    title: "9. Global Applicability",
    paragraphs: [
      "This Affiliate Disclosure is intended to comply with applicable transparency and consumer protection principles, including general guidelines issued by regulatory bodies such as:",
    ],
    bullets: [
      "U.S. Federal Trade Commission (FTC)",
      "UK Advertising Standards Authority (ASA)",
      "Consumer protection frameworks in India and other jurisdictions",
    ],
    extraParagraphs: [
      "This disclosure applies to users accessing the platform from any country.",
    ],
  },
  {
    title: "10. Changes to This Disclosure",
    paragraphs: [
      "We reserve the right to update, modify, or revise this Affiliate Disclosure at any time without prior notice. Changes will be reflected with an updated \"Last Updated\" date at the top of this page.",
    ],
    extraParagraphs: [
      "Continued use of the platform after updates constitutes acceptance of the revised disclosure.",
    ],
  },
  {
    title: "11. Contact Us",
    paragraphs: [
      "If you have any questions regarding our affiliate relationships, partnerships, or disclosures, please contact us at:",
    ],
    contactLines: [
      { label: "Email", value: "travel@rethinkways.com", href: "mailto:travel@rethinkways.com" },
    ],
  },
];

// ─── Style helpers ────────────────────────────────────────────────────────────

const textPrimary = (dark) => ({ color: dark ? "#ffffff" : "rgb(13, 19, 23)" });
const textMuted   = (dark) => ({ color: dark ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" });

// ─── Component ────────────────────────────────────────────────────────────────

export default function AffiliateDisclosure() {
  const isDark = useSyncedDarkMode();
  const container = { width: "100%", maxWidth: 1500, padding: "0 24px", margin: "0 auto" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark ? "#000000" : "#ffffff",
        color: isDark ? "#ffffff" : "rgb(13, 19, 23)",
        fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        transition: "background 200ms, color 200ms",
      }}
    >
      {/* Inject fonts scoped to this component */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Yeseva+One&display=swap');
        .trw-ad-h1 {
          font-family: 'Yeseva One', Georgia, 'Times New Roman', serif !important;
          font-weight: 400 !important;
        }
      `}</style>

      <main style={{ padding: "40px 0 0" }}>

        {/* ── Hero ── */}
        <section style={container}>
       
          {/* Page title — Yeseva One */}
          <h1
            className="trw-ad-h1"
            style={{
              margin: 0,
              fontFamily: "'Yeseva One', Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "41px",
              lineHeight: "51px",
              letterSpacing: 0,
              ...textPrimary(isDark),
            }}
          >
            Affiliate Disclosure
          </h1>

          {/* Last Updated */}
          <p
            style={{
              margin: "14px 0 0",
              fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "29px",
              ...textPrimary(isDark),
            }}
          >
            <strong style={{ fontWeight: 600 }}>Last Updated:</strong> 19 February 2026
          </p>

          {/* Intro paragraphs */}
          <div style={{ marginTop: "20px", display: "grid", gap: "22px", marginBottom: "36px" }}>
            {[
              'Travel Rethink Ways ("we," "our," "us," or "the Platform") is committed to transparency, integrity, and maintaining the trust of our readers, partners, and global travel community. This Affiliate Disclosure explains how we may earn compensation through certain links, partnerships, and promotional content featured on **Travel.RethinkWays.com** and associated digital platforms.',
              "This disclosure applies to all visitors and users of our website, regardless of geographic location.",
            ].map((line, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  fontSize: "17px",
                  lineHeight: "30px",
                  ...textMuted(isDark),
                }}
              >
                <Rich>{line}</Rich>
              </p>
            ))}
          </div>
        </section>

        {/* ── Article Sections ── */}
        <article style={container}>
          {sections.map((section, idx) => (
            <section
              key={idx}
              style={{
                borderTop: 0,
                marginTop: idx === 0 ? 0 : "36px",
              }}
            >
              {/* Section heading */}
              {section.title && (
                <h2
                  style={{
                    margin: "0 0 12px",
                    fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: "28px",
                    lineHeight: "39px",
                    ...textPrimary(isDark),
                  }}
                >
                  {section.title}
                </h2>
              )}

              {/* Paragraphs */}
              {section.paragraphs && section.paragraphs.map((para, i) => (
                <p
                  key={i}
                  style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Extra label (before bullets) */}
              {section.extraLabel && (
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{section.extraLabel}</Rich>
                </p>
              )}

              {/* Bullet list */}
              {section.bullets && section.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{bullet}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra list label (second list intro) */}
              {section.extraListLabel && (
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{section.extraListLabel}</Rich>
                </p>
              )}

              {/* Extra list (second bullet list) */}
              {section.extraList && section.extraList.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.extraList.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{item}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {/* Extra paragraphs */}
              {section.extraParagraphs && section.extraParagraphs.map((para, i) => (
                <p
                  key={i}
                  style={{
                    margin: "10px 0 0",
                    fontSize: "17px",
                    lineHeight: "30px",
                    ...textMuted(isDark),
                  }}
                >
                  <Rich>{para}</Rich>
                </p>
              ))}

              {/* Contact lines */}
              {section.contactLines && section.contactLines.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {section.contactLines.map((line, i) => (
                    <p
                      key={i}
                      style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}
                    >
                      <strong style={{ fontWeight: 600, ...textPrimary(isDark) }}>
                        {line.label}:
                      </strong>{" "}
                      {line.href ? (
                        <a
                          href={line.href}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            ...textPrimary(isDark),
                          }}
                        >
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}
